import { ChartAxis } from './ChartAxis';

export interface ChartSeries {
  name: string;
  color: string;
  values: number[];
}

// Same fade a highlighted legend entry applies elsewhere (LinePlot) - see
// its own DIMMED comment.
const DIMMED = 0.2;

// "All signals" view - each bucket stacks one segment per signal type,
// colored with that signal's own colorHex, so the mix is visible at a
// glance instead of collapsing every signal into one undifferentiated bar.
export function StackedBarChart({
  series,
  height = 180,
  highlightKey = null,
  selected = [],
  onSelect,
}: {
  series: ChartSeries[];
  height?: number;
  highlightKey?: string | null;
  // The pinned (clicked) segment, by series name + bucket index.
  selected?: { key: string; index: number }[];
  onSelect?: (key: string, index: number) => void;
}) {
  const bucketCount = series[0]?.values.length ?? 0;
  const totals = Array.from({ length: bucketCount }, (_, i) => series.reduce((sum, s) => sum + (s.values[i] ?? 0), 0));
  const max = Math.max(...totals, 1);

  return (
    <ChartAxis max={max} height={height}>
      <div className="flex items-end gap-2.5 w-full h-full">
        {Array.from({ length: bucketCount }, (_, i) => {
          const total = totals[i];
          const barHeightPct = total > 0 ? Math.max(8, (total / max) * 100) : 0;
          return (
            <div
              key={i}
              className="flex-1 flex flex-col-reverse rounded-t-lg overflow-hidden"
              style={{ height: `${barHeightPct}%` }}
            >
              {series?.map((s) => {
                const value = s.values[i] ?? 0;
                if (value <= 0) return null;
                const isSelected = selected.some((x) => x.key === s.name && x.index === i);
                return (
                  <button
                    key={s.name}
                    type="button"
                    disabled={!onSelect}
                    onClick={() => onSelect?.(s.name, i)}
                    className={onSelect ? 'cursor-pointer' : ''}
                    style={{
                      flexGrow: value,
                      background: s.color,
                      opacity: highlightKey != null && s.name !== highlightKey ? DIMMED : 1,
                      boxShadow: isSelected ? 'inset 0 0 0 2px rgba(0,0,0,0.6)' : undefined,
                    }}
                  />
                );
              })}
            </div>
          );
        })}
      </div>
    </ChartAxis>
  );
}
